import { useTranslation } from "react-i18next"

import { ArrowRight, Brain, Database, MessageSquare, Search, User } from "@/components/icons"
import { cn } from "@/lib/utils"

function FlowStep({
  icon: Icon,
  label,
  className,
}: {
  icon: typeof User
  label: string
  className?: string
}) {
  return (
    <div className={cn("flex flex-col items-center gap-1.5", className)}>
      <div className="bg-card flex h-12 w-12 items-center justify-center rounded-xl border shadow-sm">
        <Icon className="h-5 w-5" />
      </div>
      <span className="text-center text-xs font-medium">{label}</span>
    </div>
  )
}

function FlowArrow() {
  return <ArrowRight className="text-muted-foreground mt-1 h-5 w-5 shrink-0" />
}

export function AIFlowDiagram() {
  const { t } = useTranslation("", { keyPrefix: "training.components.ai_flow" })

  return (
    <div className="flex flex-col gap-8">
      {/* Standard AI flow */}
      <div>
        <p className="mb-3 text-sm font-semibold">{t("standard.title", "Standard AI Flow")}</p>
        <div className="bg-muted/30 flex items-center justify-center gap-3 rounded-xl border p-6">
          <FlowStep icon={User} label={t("standard.question", "Question")} />
          <FlowArrow />
          <FlowStep className="text-primary" icon={Brain} label={t("standard.ai", "AI")} />
          <FlowArrow />
          <FlowStep icon={MessageSquare} label={t("standard.answer", "Answer")} />
        </div>
      </div>

      {/* RAG flow */}
      <div>
        <p className="mb-3 text-sm font-semibold">
          {t("rag.title", "RAG Flow (Retrieval-Augmented Generation)")}
        </p>
        <div className="bg-muted/30 flex flex-wrap items-center justify-center gap-3 rounded-xl border p-6">
          <FlowStep icon={User} label={t("rag.question", "1. Question")} />
          <FlowArrow />
          <FlowStep icon={Search} label={t("rag.search", "2. Search")} />
          <FlowArrow />
          <FlowStep icon={Database} label={t("rag.documents", "3. Documents")} />
          <FlowArrow />
          <FlowStep className="text-primary" icon={Brain} label={t("rag.ai", "4. AI + Context")} />
          <FlowArrow />
          <FlowStep icon={MessageSquare} label={t("rag.answer", "5. Answer")} />
        </div>
        <p className="text-muted-foreground mt-2 text-xs">
          {t(
            "rag.note",
            "RAG may take a few seconds longer, but answer quality is higher because responses are grounded in relevant internal data."
          )}
        </p>
      </div>
    </div>
  )
}
